/**
 **@author       yontaek@zumper.com (Yon Taek Chung)
 **@copyright    Copyright (c) 2024, Zumper
 */

import { Box, Flex, Heading, Text } from '@chakra-ui/react'
import { CarouselV3 } from '@zcode-ui/containers'
import { selectIsMobile } from '@zcode-ui/web-zumper-global-state'
import { POPULAR_CITIES_IMPRESSION } from '@zcode-ui/web-zumper-integrations'
import { HOMEPAGE } from '@zcode/mixpanel'
import React from 'react'
import { useSelector } from 'react-redux'
import { ProgressDots } from '../ProgressDots/ProgressDots'
import { useHomepageImpression } from '../hooks/useHomepageImpression'
import { CityCard } from './CityCard'
import { HOMEPAGE_POPULAR_CITIES } from './constants'

const DesktopContent = () => (
  <Flex
    columnGap={{ desktop: 6, lg: 4, xl: 6 }}
    rowGap={{ desktop: 8, lg: 10 }}
    flexWrap="wrap"
    justifyContent={'flex-start'}
    w="100%"
  >
    {HOMEPAGE_POPULAR_CITIES.map(({ image, text, url }) => {
      return <CityCard key={text} image={image} text={text} url={url} />
    })}
  </Flex>
)

export const POPULAR_CITIES_MOBILE = [
  HOMEPAGE_POPULAR_CITIES.slice(0, 6),
  HOMEPAGE_POPULAR_CITIES.slice(6, 12),
  HOMEPAGE_POPULAR_CITIES.slice(12),
] as const

const MobileContent = () => (
  <Box<'div'> sx={{ 'div div div': { bottom: 0 } }} w="100%">
    <CarouselV3 isMobile ProgressDots={ProgressDots}>
      {POPULAR_CITIES_MOBILE.map((group, idx) => (
        <Flex
          key={idx}
          columnGap={4}
          rowGap={6}
          flexWrap="wrap"
          justifyContent={'flex-start'}
          w="100%"
          pb="38px"
          mx={6}
        >
          {group.map(({ image, text, url }) => {
            return <CityCard key={text} image={image} text={text} url={url} />
          })}
        </Flex>
      ))}
    </CarouselV3>
  </Box>
)

const PopularCities = () => {
  const isMobile = useSelector(selectIsMobile)
  const ref = useHomepageImpression({
    name: POPULAR_CITIES_IMPRESSION,
    mixpanelSection: HOMEPAGE.POPULAR_CITIES,
  })

  return (
    <Flex ref={ref} mx={{ base: '-24px', desktop: 0 }} alignItems="center" flexDir="column">
      <Flex flexDir="column" mb={8} px={{ base: 6, desktop: 0 }} w="100%">
        <Heading<'h2'> as="h2" mb={2} variant="display28To32">
          Popular cities
        </Heading>
        <Text color="gray.950" variant="body16Reg">
          Discover houses and apartments for rent in our most popular locations.
        </Text>
      </Flex>
      {isMobile ? <MobileContent /> : <DesktopContent />}
    </Flex>
  )
}

export default PopularCities
